import * as React from 'react';
import Profile, { AiMatch } from './Profile';
import { Pagination } from '../../../shared/components/navigation';

export const ProfileListContext = React.createContext({});

interface BatchProgress {
  current: number;
  total: number;
}

interface ProgressCircleProps {
  current: number;
  total: number;
}

// Simple CSS spinner component as fallback
const SimpleSpinner = ({ size = 16, color = '#186fc9' }) => (
  <div style={{
    display: 'inline-block',
    width: size,
    height: size,
    border: `2px solid ${color}33`,
    borderTop: `2px solid ${color}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginRight: 8
  }} />
);

// Add CSS keyframes for spinner animation (will be injected if not present)
const injectSpinnerCSS = () => {
  const styleId = 'spinner-keyframes';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }
};

// Inject spinner CSS when component loads
if (typeof window !== 'undefined') {
  injectSpinnerCSS();
}

const inviteCandidates = ({ job, is_temp, authenticity_token, callback, ids }: any) => {
  const url = is_temp
    ? `/jobs/${job.id}/request.json`
    : `/jobs/${job.id}/invite.json`;

  const options = {
    method: 'POST',
    redirect: 'manual' as RequestRedirect,
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': authenticity_token
    },
    body: is_temp
      ? JSON.stringify({ temp_requests: ids })
      : JSON.stringify({ invitees: ids })
  }

  fetch(url, options)
    .then(result => result.json())
    .then(result => {
      // API returns id if temp, profile_id if not
      const callback_results = is_temp ?
        result.map((r: any) => r.id) :
        result.map((r: any) => r.profile_id)

      callback(callback_results)
    })
}

const arrayEquals = (a: any[], b: any[]) => {
  return Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((val, index) => val === b[index]);
}

export const ListContext = React.createContext<any>({});

interface ProfileListProps {
  profiles: any[];
  results?: any[];
  authenticity_token: string;
  favorites: any[];
  job?: any;
  requested?: any[];
  accepted?: any[];
  declined?: any[];
  cancelled?: any[];
  user_id?: number;
  blockList?: boolean;
  blocked?: any[];
  showPositionTypes?: boolean;
  applicantIds?: number[];
  fetchUrl?: string;
  // Admin recommended tab only, keyed by profile id. Employer pages never pass it.
  aiMatches?: Record<string, AiMatch>;
}

const ProfilesList: React.FC<ProfileListProps> = ({
  profiles,
  results = [],
  authenticity_token,
  favorites,
  job = undefined,
  requested = [],
  accepted = [],
  declined = [],
  cancelled = [],
  user_id = undefined,
  blockList = false,
  blocked = [],
  showPositionTypes = true,
  applicantIds = [],
  fetchUrl,
  aiMatches
}) => {
  const isServerMode = !!fetchUrl;

  const PER_PAGE = 25;
  const [currentPage, setCurrentPage] = React.useState(1);

  // Server-side pagination state
  const [serverProfiles, setServerProfiles] = React.useState<any[]>([]);
  const [serverPagination, setServerPagination] = React.useState({ current_page: 1, total_pages: 1, total_count: 0 });
  const [fetchLoading, setFetchLoading] = React.useState(isServerMode);
  const [serverRequested, setServerRequested] = React.useState<any[]>(requested);
  const [serverAccepted, setServerAccepted] = React.useState<any[]>(accepted);
  const [serverDeclined, setServerDeclined] = React.useState<any[]>(declined);
  const [serverCancelled, setServerCancelled] = React.useState<any[]>(cancelled);
  const [serverFavorites, setServerFavorites] = React.useState<any[]>(favorites);
  const [serverBlocked, setServerBlocked] = React.useState<any[]>(blocked);
  const [serverApplicantIds, setServerApplicantIds] = React.useState<number[]>(applicantIds);

  // Fetch page data from server
  React.useEffect(() => {
    if (!isServerMode) return;

    setFetchLoading(true);
    const separator = fetchUrl.includes('?') ? '&' : '?';
    fetch(`${fetchUrl}${separator}page=${currentPage}`, {
      headers: { 'Accept': 'application/json' }
    })
      .then(res => {
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        return res.json();
      })
      .then(data => {
        setServerProfiles(data.profiles || []);
        setServerPagination(data.pagination || { current_page: 1, total_pages: 1, total_count: 0 });
        setServerRequested(data.requested || []);
        setServerAccepted(data.accepted || []);
        setServerDeclined(data.declined || []);
        setServerCancelled(data.cancelled || []);
        setServerFavorites(data.favorites || favorites);
        setServerBlocked(data.blocked || blocked);
        setServerApplicantIds(data.applicantIds || []);
        setFetchLoading(false);
      })
      .catch(() => {
        setFetchLoading(false);
      });
  }, [isServerMode, fetchUrl, currentPage]);

  // Resolve which data to use based on mode
  const activeProfiles = isServerMode ? serverProfiles : profiles;
  const activeRequested = isServerMode ? serverRequested : requested;
  const activeAccepted = isServerMode ? serverAccepted : accepted;
  const activeDeclined = isServerMode ? serverDeclined : declined;
  const activeCancelled = isServerMode ? serverCancelled : cancelled;
  const activeFavorites = isServerMode ? serverFavorites : favorites;
  const activeBlocked = isServerMode ? serverBlocked : blocked;
  const activeApplicantIds = isServerMode ? serverApplicantIds : applicantIds;

  const [state, setState] = React.useState({
    requested,
    accepted,
    declined,
    loading: false,
    requestingAll: false,
    batchProgress: null as BatchProgress | null
  })

  // Sync state when server data changes
  React.useEffect(() => {
    if (isServerMode) {
      setState(prev => ({
        ...prev,
        requested: serverRequested,
        accepted: serverAccepted,
        declined: serverDeclined
      }));
    }
  }, [isServerMode, serverRequested, serverAccepted, serverDeclined]);

  const [context, setContext] = React.useState({ blocked: activeBlocked })

  // Sync blocked context when server data changes
  React.useEffect(() => {
    if (isServerMode) {
      setContext({ blocked: serverBlocked });
    }
  }, [isServerMode, serverBlocked]);

  const addToRequested = (id: any) =>
    setState({ ...state, requested: [...state.requested, id] });

  const addAllToRequested = (ids: any[]) => {
    setState({ ...state, requested: [...state.requested, ...ids], loading: false, requestingAll: false, batchProgress: null });
  }

  const updateBatchProgress = (current: number, total: number) => {
    setState(prevState => {
      return { ...prevState, batchProgress: { current, total } };
    });
  };

  const getSelectedCandidateIds = () => {
    // For "Invite All" functionality, return all available profile IDs that aren't blocked
    return ids.filter((item: any) => context.blocked.indexOf(item) < 0);
  };

  const getProfileProcessingState = (profileId: number) => {
    // Returns: 'idle', 'pending', 'processing', 'completed'
    if (!state.loading || !state.batchProgress) return 'idle';

    const selectedIds = getSelectedCandidateIds();
    const profileIndex = selectedIds.indexOf(profileId);

    if (profileIndex === -1) return 'idle';

    const BATCH_SIZE = 25;
    const profileBatchIndex = Math.floor(profileIndex / BATCH_SIZE);
    const currentBatchIndex = Math.floor(state.batchProgress.current / BATCH_SIZE);

    if (profileIndex < state.batchProgress.current) {
      return 'completed';
    } else if (profileBatchIndex === currentBatchIndex) {
      return 'processing';
    } else if (profileBatchIndex > currentBatchIndex) {
      return 'pending';
    }

    return 'idle';
  };

  const isProfileBeingProcessed = (profileId: number) => {
    // Keep this for backwards compatibility
    const state = getProfileProcessingState(profileId);
    return state === 'processing' || state === 'pending';
  };

  const sendInviteBatch = async (candidateIds: number[]) => {
    const url = is_temp
      ? `/jobs/${job.id}/request.json`
      : `/jobs/${job.id}/invite.json`;

    const options = {
      method: 'POST',
      redirect: 'manual' as RequestRedirect,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': authenticity_token
      },
      body: is_temp
        ? JSON.stringify({ temp_requests: candidateIds })
        : JSON.stringify({ invitees: candidateIds })
    }

    const response = await fetch(url, options);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    // API returns id if temp, profile_id if not
    const invitedIds = is_temp ?
      result.map((r: any) => r.id) :
      result.map((r: any) => r.profile_id);

    return invitedIds || candidateIds;
  };

  const inviteCandidatesWithBatches = async () => {
    const selectedIds = getSelectedCandidateIds();

    if (selectedIds.length === 0) {
      return;
    }

    // Set loading state immediately with visual feedback
    setState(prevState => {
      return {
        ...prevState,
        loading: true,
        requestingAll: true,
        batchProgress: { current: 0, total: selectedIds.length }
      };
    });

    const BATCH_SIZE = 25;
    const batches: number[][] = [];

    // Split selected IDs into batches
    for (let i = 0; i < selectedIds.length; i += BATCH_SIZE) {
      batches.push(selectedIds.slice(i, i + BATCH_SIZE));
    }

    let allInvitedIds: number[] = [];

    try {
      // Process batches sequentially
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];

        // Update progress at the start of each batch
        const currentProgress = batchIndex * BATCH_SIZE;
        updateBatchProgress(currentProgress, selectedIds.length);

        try {
          const invitedIds = await sendInviteBatch(batch);
          allInvitedIds = [...allInvitedIds, ...invitedIds];

          // Update progress after batch completion
          const completedProgress = Math.min((batchIndex + 1) * BATCH_SIZE, selectedIds.length);
          updateBatchProgress(completedProgress, selectedIds.length);
        } catch {
          // Continue processing remaining batches even if one fails.
        }

        // No delay - process batches as fast as possible
      }

      if (allInvitedIds.length > 0) {
        addAllToRequested(allInvitedIds);
      } else {
      }
    } catch {
      setState({ ...state, loading: false, requestingAll: false, batchProgress: null });
    }
  };

  const ProgressCircle: React.FC<ProgressCircleProps> = ({ current, total }) => {
    const percentage = Math.round((current / total) * 100);
    const circumference = 2 * Math.PI * 35; // increased radius to 35 for maximum visibility
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 15,
        marginBottom: 15
      }}>
        <div style={{ position: 'relative', width: 100, height: 100, marginBottom: 15 }}>
          <svg width="100" height="100" style={{ transform: 'rotate(-90deg)' }}>
            <circle
              cx="50"
              cy="50"
              r="35"
              stroke="#e9ecef"
              strokeWidth="8"
              fill="transparent"
            />
            <circle
              cx="50"
              cy="50"
              r="35"
              stroke="#186fc9"
              strokeWidth="8"
              fill="transparent"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              style={{
                transition: 'stroke-dashoffset 0.8s ease',
                filter: 'drop-shadow(0 3px 6px rgba(24, 111, 201, 0.4))'
              }}
            />
          </svg>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#186fc9',
            textShadow: '0 1px 2px rgba(0,0,0,0.1)'
          }}>
            {percentage}%
          </div>
        </div>
        <div style={{
          fontSize: '18px',
          color: '#495057',
          fontWeight: '600',
          textAlign: 'center',
          marginBottom: '8px'
        }}>
          Processing {current} of {total} candidates
        </div>
        <div style={{
          fontSize: '14px',
          color: '#6c757d',
          textAlign: 'center',
          lineHeight: '1.4'
        }}>
          <i className="fad fa-clock" style={{ marginRight: '5px' }}></i>
          Please wait while we process your invitations...
        </div>
        <div style={{
          fontSize: '12px',
          color: '#186fc9',
          marginTop: '10px',
          textAlign: 'center',
          fontStyle: 'italic'
        }}>
          Processing candidates efficiently...
        </div>
      </div>
    );
  };

  const is_temp = job && job.position_type == 'temp';
  // Timezone-safe: parse temp_date as local date
  const isPastTempDate = (() => {
    if (!is_temp || !job.temp_date) return false;
    const dateParts = job.temp_date.toString().split('T')[0].split('-');
    const tempDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return tempDate < today;
  })();

  const ids = activeProfiles.map(profile => profile.id);

  // Pagination: server-side vs client-side
  const totalPages = isServerMode
    ? serverPagination.total_pages
    : Math.ceil(activeProfiles.length / PER_PAGE);
  const totalCount = isServerMode
    ? serverPagination.total_count
    : activeProfiles.length;
  const needsPagination = totalPages > 1;
  const startIndex = isServerMode ? (currentPage - 1) * PER_PAGE : (currentPage - 1) * PER_PAGE;
  const paginatedProfiles = isServerMode
    ? activeProfiles
    : (needsPagination ? activeProfiles.slice(startIndex, startIndex + PER_PAGE) : activeProfiles);

  // Reset to page 1 if current page exceeds total (e.g. after invite-all changes the list)
  React.useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // Only check against non-blocked candidates for "requested all" status
  const availableIds = ids.filter((item: any) => context.blocked.indexOf(item) < 0);
  const requested_all = arrayEquals(state.requested.sort(), availableIds.sort());

  // Loading state for server-side mode
  if (isServerMode && fetchLoading) {
    return (
      <div className="profile-list-modern">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <SimpleSpinner size={32} />
          <div style={{ marginTop: '0.5rem', color: '#6c757d' }}>Loading profiles...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-list-modern" key={isServerMode ? `server-${currentPage}` : JSON.stringify(requested)}>
      <ListContext.Provider value={[context, setContext]}>
        {/* Always show button area - hide in server mode (invited tab doesn't need Invite All) */}
        {!isServerMode && job && !job.closed && !job.temp_filled && !isPastTempDate && (
          <div style={{ width: '100%', textAlign: 'right', marginBottom: '1rem' }}>
            {state.loading ? (
              <button className="btn-modern btn-modern--primary" disabled style={{
                opacity: 0.8,
                cursor: 'not-allowed'
              }}>
                <i className="fad fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                {state.batchProgress ?
                  `Processing ${state.batchProgress.current}/${state.batchProgress.total}...` :
                  'Starting...'
                }
              </button>
            ) : requested_all && profiles.length > 0 ? (
              <button className="btn-modern btn-modern--success" disabled>
                <i className="fad fa-check"></i> {is_temp ? "Requested" : "Invited"} All
              </button>
            ) : (
              <button className="btn-modern btn-modern--primary"
                onClick={inviteCandidatesWithBatches}
                style={{ cursor: 'pointer' }}
              >
                <i className="fad fa-users"></i> {is_temp ? "Request" : "Invite"} All
              </button>
            )}
          </div>
        )}

        {/* Large progress indicator when processing */}
        {state.loading && state.batchProgress && (
          <div className="alert-modern alert-modern--info" style={{ marginBottom: '1.5rem' }}>
            <div className="alert-modern__content" style={{ width: '100%', textAlign: 'center' }}>
              <ProgressCircle
                current={state.batchProgress.current}
                total={state.batchProgress.total}
              />
            </div>
          </div>
        )}
        <ProfileListContext.Provider value={{ blockList }}>
          {needsPagination && (
            <div style={{ padding: '0.5rem 0', fontSize: '0.875rem', color: '#6c757d' }}>
              Showing {startIndex + 1}–{Math.min(startIndex + PER_PAGE, totalCount)} of {totalCount} {isServerMode ? (is_temp ? 'requested' : 'invited') : 'matched'} profiles
            </div>
          )}
          <ul className="profiles-grid job-list full border-bottom">
            {paginatedProfiles.map(profile => {
              const result = results.find(r => r._id == profile.id);
              const distance = result ? result.fields.distance : false;
              const isApplicant = activeApplicantIds.includes(profile.id);
              const isCancelled = activeCancelled.includes(profile.id);
              const isDeclined = state.declined.includes(profile.id);
              return <Profile
                key={profile.id}
                profile={profile}
                authenticity_token={authenticity_token}
                favorites={activeFavorites}
                job={job}
                requested={state.requested}
                accepted={state.accepted}
                declined={state.declined}
                cancelled={activeCancelled}
                addToRequestedFunction={addToRequested}
                distance={distance}
                user_id={user_id}
                requestingAll={state.requestingAll}
                isBeingProcessed={isProfileBeingProcessed(profile.id)}
                processingState={getProfileProcessingState(profile.id)}
                showPositionTypes={showPositionTypes}
                isApplicant={isApplicant}
                isCancelled={isCancelled}
                isDeclined={isDeclined}
                aiMatch={aiMatches?.[String(profile.id)]}
              />
            })}
          </ul>
          {needsPagination && (
            <Pagination
              current_page={currentPage}
              total_pages={totalPages}
              records={totalCount}
              prev_page={Math.max(1, currentPage - 1)}
              next_page={Math.min(totalPages, currentPage + 1)}
              navigateTo={(page) => {
                setCurrentPage(page);
                // Scroll to top of list
                const listEl = document.querySelector('.profile-list-modern');
                if (listEl) listEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            />
          )}
        </ProfileListContext.Provider>
      </ListContext.Provider>
    </div>
  );
}

export default ProfilesList;
